import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Camera } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { coreAPI, type Company } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

export function CompanyTab() {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    address_line_1: '',
    website: '',
    primary_phone: '',
    primary_email: '',
  });

  useEffect(() => {
    if (user?.company) {
      loadCompany(user.company);
    }
  }, [user?.company]);

  const loadCompany = async (companyId: number) => {
    try {
      setLoading(true);
      const response = await coreAPI.getCompany(companyId);
      const c = response.data;
      setCompany(c);
      setForm({
        name: c.name || '',
        description: c.description || '',
        address_line_1: c.address_line_1 || '',
        website: c.website || '',
        primary_phone: c.primary_phone || '',
        primary_email: c.primary_email || '',
      });
    } catch (error) {
      console.error('Failed to load company:', error);
    } finally {
      setLoading(false);
    }
  };

  const onLogoFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !company) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setUploadingLogo(true);
      const response = await coreAPI.uploadCompanyLogo(company.id, file);
      setCompany(response.data);
      toast.success('Logo updated');
    } catch (err: unknown) {
      console.error('Failed to upload logo:', err);
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!company) return;
    try {
      setSaving(true);
      await coreAPI.updateCompany(company.id, form);
      toast.success('Company information updated');
    } catch (error) {
      console.error('Failed to update company:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update company information'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Information</CardTitle>
        <CardDescription>
          Manage your company details and settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Company Logo</Label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0 p-2">
              {(company as unknown as Record<string, unknown>)?.['logo_url'] ? (
                <img
                  src={(company as unknown as Record<string, string>)['logo_url']}
                  alt={form.name || 'Company logo'}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <span className="text-2xl font-semibold text-muted-foreground">
                  {(form.name || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onLogoFileChosen}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Uploading...</>
                ) : (
                  <><Camera className="h-4 w-4 mr-2" />Upload New Logo</>
                )}
              </Button>
              <p className="text-xs text-muted-foreground">PNG or JPG, up to 5MB.</p>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            value={form.address_line_1}
            onChange={e => setForm({ ...form, address_line_1: e.target.value })}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={form.website}
              onChange={e => setForm({ ...form, website: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyPhone">Phone</Label>
            <Input
              id="companyPhone"
              type="tel"
              value={form.primary_phone}
              onChange={e => setForm({ ...form, primary_phone: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="companyEmail">Email</Label>
          <Input
            id="companyEmail"
            type="email"
            value={form.primary_email}
            onChange={e => setForm({ ...form, primary_email: e.target.value })}
          />
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Update Company Info'}
        </Button>
      </CardContent>
    </Card>
  );
}
